import { VITE_NODE_ENV } from '@/utils/getEnv';
import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';

interface ChartDataPoint {
  year: string;
  cashFlow: number;
  cumulative: number;
  transparentBottom: number;
  yellowMiddle: number;
  transparentTop: number;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    payload: ChartDataPoint;
    value: number;
    dataKey: string;
  }>;
  label?: string;
}

const clientBarChartColors = {
  'leonhard-weiss': '#FECC00',
  gridty: '#8F94FF',
};

const color = VITE_NODE_ENV['VITE_THEME'] as keyof typeof clientBarChartColors;

const AmortizationChart = ({ report: reportRaw }: { report: any }) => {
  function safeParseReport(reportRaw: any): any {
    if (!reportRaw) {
      // report is undefined, null, empty string, or falsy
      return {}; // return empty object as safe fallback
    }

    if (typeof reportRaw === 'string') {
      try {
        return JSON.parse(reportRaw);
      } catch (e) {
        console.error('Failed to parse report string:', e);
        return {}; // safe fallback if JSON.parse fails
      }
    }

    // If already an object, return as is
    return reportRaw;
  }

  const report = safeParseReport(reportRaw);

  const cashFlows: number[] = report.y || report.y1 || [];

  // Calculate cumulative values starting from initial investment
  const initialInvestment: number = Math.min(0, ...(cashFlows || 0));
  let cumulativeValue: number = initialInvestment;

  // Calculate Y-axis range from actual data
  const allValues = [...cashFlows];
  let tempCumulative = initialInvestment;
  const cumulativeValues = cashFlows?.map(cf => {
    tempCumulative += cf;
    return tempCumulative;
  });
  // allValues.push(...cumulativeValues);
  // allValues.push(initialInvestment);

  const maxValue: number = Math.max(...allValues);
  const minValue: number = Math.min(...allValues);

  // Prepare data for the chart
  const data: ChartDataPoint[] = cashFlows?.map((cashFlow: number, index: number) => {
    cumulativeValue += cashFlow;

    // Calculate bar segments for full-height bars
    // The key insight: we need to create bars that stack to the total chart height
    const totalHeight: number = maxValue - minValue; // 577464.65

    let transparentBottom: number = 0;
    let yellowMiddle: number = 0;
    let transparentTop: number = 0;

    if (cashFlow >= 0) {
      // For positive cash flows:
      // Bottom: from minValue (-173407.95) to 0 = 173407.95
      transparentBottom = Math.abs(minValue);
      // Middle: the cash flow value itself
      yellowMiddle = cashFlow;
      // Top: from cashFlow to maxValue
      transparentTop = maxValue - cashFlow;
    } else {
      // For negative cash flows:
      // Bottom: from minValue to the cashFlow point
      transparentBottom = Math.abs(minValue) + cashFlow; // cashFlow is negative, so this reduces the bottom
      // Middle: the absolute cash flow amount
      yellowMiddle = Math.abs(cashFlow);
      // Top: from 0 to maxValue
      transparentTop = maxValue;
    }

    // Verify total height
    const calculatedTotal = transparentBottom + yellowMiddle + transparentTop;

    return {
      year: String(index + 1).padStart(2, '0'),
      cashFlow: cashFlow,
      cumulative: cumulativeValue,
      transparentBottom: transparentBottom,
      yellowMiddle: yellowMiddle,
      transparentTop: transparentTop,
    };
  });

  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="text-sm font-medium">{`Jahr: ${label}`}</p>
          <p className="text-sm text-blue-600">{`Cash-Flow: €${data.cashFlow.toLocaleString('de-DE')}`}</p>
          {/* <p className="text-sm text-green-600">{`Kumuliert: €${data.cumulative.toLocaleString('de-DE')}`}</p> */}
        </div>
      );
    }
    return null;
  };

  const formatYAxis = (value: number): string => {
    return value.toLocaleString('de-DE');
  };

  return (
    <div className="w-full bg-white">
      <h2 className="text-lg font-medium text-gray-800 mt-2 mb-4">Amortisation</h2>

      <div style={{ width: '100%', height: '280px' }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeWidth={0} />
            <XAxis
              dataKey="year"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              label={{
                value: 'Jahr',
                position: 'insideBottom',
                offset: -10,
                style: { textAnchor: 'middle', fill: '#666' },
              }}
            />
            <YAxis
              domain={['dataMin', 'dataMax']}
              tickFormatter={formatYAxis}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              label={{
                value: 'Gewinn in €',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#666' },
              }}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Bottom transparent segment */}
            <Bar
              dataKey="transparentBottom"
              stackId="fullHeight"
              fill="#f0f0f0"
              stroke="#e0e0e0"
              radius={9}
              barSize={14}
            />

            {/* Middle yellow segment (the actual cash flow) */}
            <Bar
              dataKey="yellowMiddle"
              stackId="fullHeight"
              fill={clientBarChartColors[color]}
              stroke={clientBarChartColors[color]}
              radius={9}
              barSize={14}
            />

            {/* Top transparent segment */}
            <Bar
              dataKey="transparentTop"
              stackId="fullHeight"
              fill="#f0f0f0"
              stroke="#e0e0e0"
              radius={9}
              barSize={14}
            />

            {/* Cumulative line */}
            {/* <Line
              type="linear"
              dataKey="cumulative"
              stroke="#000000"
              strokeWidth={2.5}
              dot={false}
              connectNulls={true}
            /> */}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AmortizationChart;
